import { blockNoteToPlainText } from "../../../shared/utils/blockNote";

/** One member of a group session, and whether they came — null if not said yet. */
export interface AttendanceEntry {
    patientId: string;
    firstName: string;
    lastName: string;
    attended: boolean | null;
}

export interface AttendanceMark {
    patientId: string;
    attended: boolean;
}

export interface CourseModuleOption {
    id: string;
    name: string;
    /** `YYYY-MM-DD`, or null when not decided. */
    startsOn: string | null;
    endsOn: string | null;
    description: string | null;
    topics: string[];
    /** Rich text: the editor's document, as the course stores it. */
    summary: string | null;
}

/**
 * The course a mentoring belongs to — its student's, the one a group's
 * members share, or the one of the module it works on — with its modules and
 * the one assigned. `course` is null for a session whose people are in no one
 * course.
 *
 * `modules` is the plan as it reads now, to choose from. `module` is the
 * assigned one as it read when it was assigned: what the mentoring shows,
 * whatever the plan became since.
 */
export interface SessionCourse {
    course: { id: string; name: string; internalName: string } | null;
    modules: CourseModuleOption[];
    moduleId: string | null;
    module: CourseModuleOption | null;
}

/** How many of those marked came, out of the whole group. */
export function attendanceSummary(entries: AttendanceEntry[]): { attended: number; marked: number; total: number } {
    return {
        attended: entries.filter((entry) => entry.attended === true).length,
        marked: entries.filter((entry) => entry.attended !== null).length,
        total: entries.length,
    };
}

/** Whether the session's day and hour have come, on the therapist's clock. No hour is the start of its day. */
export function hasStarted(date: string, hour: string | null | undefined, now: Date = new Date()): boolean {
    const [year, month, day] = date.slice(0, 10).split("-").map(Number);
    const [hours, minutes] = (hour || "00:00").split(":").map(Number);
    return new Date(year, month - 1, day, hours || 0, minutes || 0) <= now;
}

/**
 * A pending session whose time has come and that has notes: most likely it
 * took place, and the therapist is asked — never assumed: notes may also be
 * written ahead, to prepare it.
 */
export function asksToBeMarkedHeld(
    session: { session_status?: string | null; notes?: string | null; date: string; hour?: string | null },
    now: Date = new Date()
): boolean {
    return (
        (session.session_status ?? "pending") === "pending" &&
        Boolean(blockNoteToPlainText(session.notes ?? "").trim()) &&
        hasStarted(session.date, session.hour, now)
    );
}

/** A pending session dated after today: one to prepare rather than to write up. */
export function isUpcoming(date: string, today: Date = new Date()): boolean {
    const day = date.slice(0, 10);
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    return day > todayKey;
}
